<script setup lang="ts">
/**
 * Chapitre « Informations » de la page publique d'une association — charte
 * « apple » (Karim 2026-10-18) : tuiles blanches sur le fond gris du
 * chapitre (carte + adresse en grand, coordonnées, horaires, documents),
 * puis la galerie en mosaïque. Le nom du fichier (« colonne d'infos ») est
 * historique : la page tenait sur deux colonnes avant la refonte. Le
 * compteur de dons est remonté sous le nom de l'association (index.vue).
 */
import {
  adresseDeclareeOrganisation,
  lienTelephone,
  telephoneAffiche,
} from "@donoor/utils";
import { aDesCoordonneesAsso, aDesInfosAsso, aUneAdresseUtile } from "~/utils/associationInfos";
import { TITRE_CARTE_APPLE } from "~/utils/titresApple";
import type { ChampsAdresseOrganisation } from "@donoor/types";
interface OrgInfo extends ChampsAdresseOrganisation {
  name: string;
  contact_email: string | null;
  contact_phone: string | null;
  /** Pays ISO de l'asso (France à défaut) : le téléphone s'y lit. */
  country?: string | null;
  website: string | null;
  full_address: string | null;
  opening_hours: string | Record<string, unknown> | null;
  social_links: { facebook?: string; instagram?: string; twitter?: string; youtube?: string; tiktok?: string; linkedin?: string } | null;
  total_raised: number;
  total_donors: number;
  display: {
    show_total_raised: boolean;
    show_total_donors: boolean;
    show_opening_hours: boolean;
  };
  documents: Array<{ id: number; title: string; file: string }>;
  gallery: Array<{ id: number; directus_files_id: string }>;
}

const props = defineProps<{
  org: OrgInfo;
  /**
   * Lien vers le hub /associations/ville/{ville} de l'asso. SEO 2026-08-29 :
   * le fil d'Ariane de la page est masqué (JSON-LD seulement), donc AUCUN lien
   * crawlable ne remontait de la fiche vers son hub — le maillage était à
   * sens unique. Un seul lien texte, discret, sous l'adresse. Jamais d'autres
   * associations ici (règle Karim : focus total sur l'asso).
   */
  hubVillePath?: string | null;
  hubVilleName?: string | null;
  assetsUrl: string;
  hasSocialLinks: boolean;
  formatAmount: (amount: number) => string;
  formatOpeningHours: (data: unknown) => string;
  getImageUrl: (fileId: string | null, width?: number, height?: number) => string | null;
}>();

// Téléphone, lu dans le PAYS de l'asso, OUTRE-MER COMPRIS (règle partagée
// @donoor/utils) :
//  - AFFICHÉ au format national du pays (« 06 96 70 34 58 », « 0470 12 34
//    56 »), international s'il est d'un autre pays ;
//  - APPELÉ via `tel:` en international avec LE BON indicatif (+596…) : un
//    numéro enregistré à tort en « +33696… » par l'ancienne règle affichait
//    juste mais appelait un numéro inexistant (2026-09-25).
// Numéro inattendu : valeur telle quelle.
const paysAsso = computed(() => props.org.country || "FR");
const displayPhone = computed(() => telephoneAffiche(props.org.contact_phone, paysAsso.value));
const telHref = computed(() => lienTelephone(props.org.contact_phone, paysAsso.value));

// Bloc « Où nous trouver ? » (lot 4) : adresse DÉCLARÉE, règle partagée
// `adresseDeclareeOrganisation` (@donoor/utils, contrat §17.7.3) — la MÊME
// que les reçus, CGV et documents de l'API, la fiche CRM du backup-manager
// et le mobile. Rue telle que l'association l'a déclarée (jamais une voie
// normalisée par un géocodeur), « CP Ville » (« Ville CP » au Royaume-Uni
// et en Irlande), mention CEDEX et complément du texte, puis, hors
// métropole, le territoire ou le pays (« 97228 Sainte-Luce » /
// « Martinique »). Code postal et ville viennent des morceaux enregistrés
// quand ils décrivent ce texte, à défaut de son analyse. Sans rien de
// lisible : le texte tel quel (garanti par la règle).
const lignesOuNousTrouver = computed(
  () => adresseDeclareeOrganisation(props.org.full_address, props.org)?.lignes ?? [],
);

// Liens externes (site web + réseaux sociaux) : champs libres saisis par
// l'asso → normalisation obligatoire. Sans protocole, « donoor.org » rendu
// dans :href devient le lien relatif /associations/donoor.org → 404 SEO
// (audit SemRush 2026-08-05).
const websiteUrl = computed(() => normalizeExternalUrl(props.org.website));
const socialUrls = computed(() => {
  const s = props.org.social_links;
  return {
    facebook: normalizeExternalUrl(s?.facebook),
    instagram: normalizeExternalUrl(s?.instagram),
    twitter: normalizeExternalUrl(s?.twitter),
    youtube: normalizeExternalUrl(s?.youtube),
    tiktok: normalizeExternalUrl(s?.tiktok),
    linkedin: normalizeExternalUrl(s?.linkedin),
  };
});

// Lightbox galerie : URLs pleine taille (fit=inside — pas de crop, contrairement
// aux miniatures 300×300 de la grille qui utilisent fit=cover).
const { t } = useI18n();

// Le chapitre n'apparaît que s'il a au moins une tuile — règle unique,
// partagée avec le lien « Informations » de la barre des sections.
const aDesCoordonnees = computed(() => aDesCoordonneesAsso(props.org, props.hasSocialLinks));
const aDesInfos = computed(() => aDesInfosAsso(props.org, props.hasSocialLinks));

// Mosaïque sans trou : la 1re photo passe en grand seulement si le reste
// remplit des rangées entières — 2 colonnes sur mobile (nombre impair de
// photos), 3 au-delà (multiple de 3).
const classePremierePhoto = computed(() => {
  const n = props.org.gallery.length;
  return [
    n > 2 && n % 2 === 1 ? "col-span-2 row-span-2" : "",
    n > 2 && n % 3 === 0 ? "sm:col-span-2 sm:row-span-2" : "sm:col-span-1 sm:row-span-1",
  ].join(" ");
});

const lightbox = useTemplateRef<{ open: (index: number) => void }>("lightbox");
const galleryFullUrls = computed(() =>
  props.org.gallery.map(
    (img) =>
      `${props.assetsUrl}/${img.directus_files_id}?width=1600&fit=inside&format=webp&quality=85`,
  ),
);
</script>


<template>
  <div>
    <AssociationsPageChapitre
      v-if="aDesInfos"
      id="section-info"
      fond="gris"
      :defilant="false"
      :titre="t('association.infoColumn.informations')"
    >
      <div class="grid gap-5 lg:grid-cols-3">
        <!-- Où nous trouver : carte + adresse, la tuile la plus large. -->
        <div v-if="aUneAdresseUtile(org)" id="section-map" class="pa-reveal overflow-hidden rounded-[28px] bg-white lg:col-span-2 lg:row-span-2">
          <ClientOnly>
            <!-- Point enregistré (exposé par l'API seulement s'il décrit
                 l'adresse actuelle) : la carte s'affiche sans géocodage. -->
            <MiniMap
              :address="org.full_address ?? ''"
              :latitude="org.geo_latitude"
              :longitude="org.geo_longitude"
              :precision="org.geo_precision"
              :pays="org.address_country || org.country"
              :territoire="org.address_territory"
              :departement="org.address_department"
              height="280px"
            />
            <template #fallback>
              <div class="flex h-[280px] items-center justify-center bg-[#e8e8ed]">
                <UIcon name="i-lucide-map" class="size-10 text-[#86868b]" />
              </div>
            </template>
          </ClientOnly>
          <div class="p-7">
            <h3 class="mb-2 text-[21px] text-[#1d1d1f]" :style="TITRE_CARTE_APPLE">{{ t("association.infoColumn.ouNousTrouver") }}</h3>
            <p v-for="(ligne, i) in lignesOuNousTrouver" :key="i" class="text-[17px] leading-[1.5] text-[#424245]">{{ ligne }}</p>
            <NuxtLink
              v-if="hubVillePath && hubVilleName"
              :to="hubVillePath"
              class="mt-3 inline-block text-[14px] font-medium text-[#86868b] transition-colors hover:text-(--color-main) hover:underline"
            >
              {{ t("association.infoColumn.associationAVille", { ville: hubVilleName }) }}
            </NuxtLink>
          </div>
        </div>

        <!-- Coordonnées + réseaux sociaux (un lien social est une coordonnée
             comme une autre — Karim 2026-09-12). -->
        <div
          v-if="aDesCoordonnees"
          class="pa-reveal rounded-[28px] bg-white p-7"
          style="--d: 90ms"
        >
          <h3 class="mb-5 text-[21px] text-[#1d1d1f]" :style="TITRE_CARTE_APPLE">{{ t("association.infoColumn.contact") }}</h3>
          <ul class="space-y-3.5">
            <li v-if="org.contact_email" class="flex items-center gap-3">
              <span class="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#f5f5f7]"><UIcon name="i-lucide-mail" class="size-4 text-[#1d1d1f]" /></span>
              <a :href="`mailto:${org.contact_email}`" class="min-w-0 truncate text-[15px] font-medium text-(--color-main) hover:underline">{{ org.contact_email }}</a>
            </li>
            <li v-if="org.contact_phone" class="flex items-center gap-3">
              <span class="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#f5f5f7]"><UIcon name="i-lucide-phone" class="size-4 text-[#1d1d1f]" /></span>
              <a :href="telHref" class="text-[15px] font-medium text-(--color-main) hover:underline">{{ displayPhone }}</a>
            </li>
            <li v-if="websiteUrl" class="flex items-center gap-3">
              <span class="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#f5f5f7]"><UIcon name="i-lucide-globe" class="size-4 text-[#1d1d1f]" /></span>
              <a :href="websiteUrl" target="_blank" rel="noopener noreferrer" class="min-w-0 truncate text-[15px] font-medium text-(--color-main) hover:underline">{{ websiteUrl.replace(/^https?:\/\//, '') }}</a>
            </li>
          </ul>
          <div v-if="hasSocialLinks" class="mt-6 flex flex-wrap gap-2">
            <a v-if="socialUrls.facebook" :href="socialUrls.facebook" target="_blank" rel="noopener noreferrer" aria-label="Facebook" class="reseau"><UIcon name="i-lucide-facebook" class="size-5" /></a>
            <a v-if="socialUrls.instagram" :href="socialUrls.instagram" target="_blank" rel="noopener noreferrer" aria-label="Instagram" class="reseau"><UIcon name="i-lucide-instagram" class="size-5" /></a>
            <a v-if="socialUrls.twitter" :href="socialUrls.twitter" target="_blank" rel="noopener noreferrer" aria-label="X" class="reseau"><UIcon name="i-lucide-twitter" class="size-5" /></a>
            <a v-if="socialUrls.youtube" :href="socialUrls.youtube" target="_blank" rel="noopener noreferrer" aria-label="YouTube" class="reseau"><UIcon name="i-lucide-youtube" class="size-5" /></a>
            <a v-if="socialUrls.tiktok" :href="socialUrls.tiktok" target="_blank" rel="noopener noreferrer" aria-label="TikTok" class="reseau"><UIcon name="i-simple-icons-tiktok" class="size-[18px]" /></a>
            <a v-if="socialUrls.linkedin" :href="socialUrls.linkedin" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" class="reseau"><UIcon name="i-lucide-linkedin" class="size-5" /></a>
          </div>
        </div>

        <!-- Horaires d'ouverture -->
        <div v-if="org.opening_hours && org.display?.show_opening_hours" id="section-opening-hours" class="pa-reveal rounded-[28px] bg-white p-7" style="--d: 140ms">
          <h3 class="mb-4 text-[21px] text-[#1d1d1f]" :style="TITRE_CARTE_APPLE">{{ t("association.infoColumn.horaires") }}</h3>
          <p class="whitespace-pre-line text-[15px] leading-[1.7] text-[#424245]">{{ typeof org.opening_hours === 'string' ? org.opening_hours : formatOpeningHours(org.opening_hours) }}</p>
        </div>

        <!-- Documents officiels -->
        <div v-if="org.documents.length > 0" class="pa-reveal rounded-[28px] bg-white p-7" style="--d: 180ms">
          <h3 class="mb-4 text-[21px] text-[#1d1d1f]" :style="TITRE_CARTE_APPLE">{{ t("association.infoColumn.documents") }}</h3>
          <div class="space-y-2">
            <a
              v-for="doc in org.documents"
              :key="doc.id"
              :href="`${assetsUrl}/${doc.file}`"
              target="_blank"
              rel="noopener noreferrer"
              class="document flex items-center gap-3 rounded-2xl bg-[#f5f5f7] px-4 py-3 no-underline"
            >
              <UIcon name="i-lucide-download" class="size-4 shrink-0 text-[#1d1d1f]" />
              <span class="truncate text-[15px] font-medium text-[#1d1d1f]">{{ doc.title }}</span>
            </a>
          </div>
        </div>
      </div>
    </AssociationsPageChapitre>

    <!-- Galerie en mosaïque : la première photo en grand. L'ancre
         `section-gallery` sert la barre des sections. -->
    <!-- Collée au chapitre « Informations » (même fond gris) : pas de
         second grand espace entre les deux. -->
    <AssociationsPageChapitre
      v-if="org.gallery.length > 0"
      id="section-gallery"
      fond="gris"
      :defilant="false"
      :class="aDesInfos && '!pt-0'"
      :titre="t('association.page.galerie')"
    >
      <div class="galerie grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <button
          v-for="(img, index) in org.gallery"
          :key="img.id"
          type="button"
          class="photo pa-reveal group relative aspect-square cursor-pointer overflow-hidden rounded-[20px] bg-[#e8e8ed]"
          :class="index === 0 ? classePremierePhoto : ''"
          :style="{ '--d': `${(index % 4) * 70}ms` }"
          :aria-label="t('association.infoColumn.agrandirPhoto', { numero: index + 1 })"
          @click="lightbox?.open(index)"
        >
          <img
            :src="getImageUrl(img.directus_files_id, index === 0 ? 900 : 450, index === 0 ? 900 : 450)!"
            :alt="t('association.infoColumn.altPhoto', { nom: org.name, numero: index + 1 })"
            class="photo-image h-full w-full object-cover"
            loading="lazy"
          />
          <span class="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/15">
            <UIcon name="i-lucide-expand" class="size-6 text-white opacity-0 drop-shadow-lg transition-opacity duration-300 group-hover:opacity-100" />
          </span>
        </button>
      </div>

      <ImageLightbox ref="lightbox" :images="galleryFullUrls" />
    </AssociationsPageChapitre>
  </div>
</template>

<style scoped>
.reseau {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #f5f5f7;
  color: #1d1d1f;
  transition:
    background-color 0.25s ease,
    color 0.25s ease,
    transform 0.25s ease;
}
.document {
  transition: background-color 0.25s ease;
}
.photo-image {
  transition: transform 0.7s cubic-bezier(0.28, 0, 0.22, 1);
}
@media (hover: hover) {
  .reseau:hover {
    background: #1d1d1f;
    color: #fff;
    transform: translateY(-2px);
  }
  .document:hover {
    background: #e8e8ed;
  }
  .photo:hover .photo-image {
    transform: scale(1.05);
  }
}
@media (prefers-reduced-motion: reduce) {
  .reseau,
  .photo-image {
    transition: none;
  }
}
</style>
